// @flow
import * as Immutable from 'immutable';
import {
    LocationTypeType,
    MasterLocation
} from 'common/models/internal/MasterLocation';
import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import LocationActions from 'common/redux/actions/location';
import {
    skill as skillsApi,
    cities as citiesApi,
    pincodes as pincodesApi,
    location as locationApi
} from 'common/middleware/api';
import { Skill } from 'common/models/internal/Skill';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import locationMasterIDs from 'common/tests/masterManagement/locationMaster.json';
import { TeamEnum } from 'common/enums/TeamEnum';
import CountryHelper from 'common/helpers/CountryHelper';
import { ENTITY_NAME_ID } from 'common/constants/entity/locationMaster';
import HomebaseIdInput from 'common/entity/homebaseMaster/HomebaseIdInput';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import DataUtils from 'common/utils/DataUtils';
import {
    MasterHomebase,
    HomebaseType
} from 'common/models/internal/MasterHomebase';
import { HomebaseId } from 'common/models/internal/HomebaseId';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { VehicleModelId } from 'common/models/internal/VehicleModelId';
import VehicleModelIdInput from 'common/entity/vehicleMaster/VehicleModelIdInput';

export const LocationTypeOptions = LocationTypeType.getOptions({
    translationKey: 'masterLocationProperties.types'
});

const formatCreateLabel = t => label => {
    return t('locationForm.createPrompt', {
        code: label
    });
};

const getActiveFilter = () => {
    return new FilterBuilder()
        .withName('status')
        .withOperation(FilterOperation.Equals)
        .withValue('ACTIVE');
};

const crossDockIdInputFilters = (input, teams) => {
    const crossdockFilter = new FilterBuilder()
        .withName('type')
        .withOperation(FilterOperation.Equals)
        .withValue(HomebaseType.Crossdock);
    let filters = new FilterListBuilder().addAll([
        getActiveFilter(),
        crossdockFilter
    ]);
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    if (teams.size) {
        filters = filters.add(
            new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValue(teams.toJS())
                .withCaseSensitive(true)
        );
    }
    return filters.build();
};

const vehicleModelIdInputFilters = input => {
    let filters = new FilterListBuilder().addAll([getActiveFilter()]);
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const LOCATION_FIELDS = [
    {
        accessor: 'code',
        testId: locationMasterIDs.addNewLocation.inputCode,
        label: 'locationForm.code',
        helpText: 'locationForm.codeTooltip',
        placeholder: 'locationForm.codePlaceholder',
        disabledOnEdit: true,
        required: true,
        halfWidth: false,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props, t }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                conflictEntities: t('entityManagement:entityMasterTitle', {
                    entityNameId: 'masterHomebase'
                }),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'locationForm.name',
        testId: locationMasterIDs.addNewLocation.inputName,
        helpText: 'locationForm.nameTooltip',
        placeholder: 'locationForm.namePlaceholder',
        required: true,
        halfWidth: false,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'teams',
        label: 'locationForm.teams',
        dependentAccessors: ['skills', 'contact', 'crossDockId'],
        helpText: 'locationForm.teamsTooltip',
        type: FieldTypeEnum.Teams
    },
    {
        accessor: 'contact',
        type: FieldTypeEnum.Contact,
        extraProps: ({ entity, t, props }) => {
            const { validateDuplicateId } = props;
            return {
                validateDuplicateId,
                translationsObject: {
                    label: t('locationForm.contact'),
                    helpText: t('locationForm.contactTooltip'),
                    addContactText: t('locationForm.addContact'),
                    overrideMessage: t('locationForm.addContactOverride')
                },
                teamIds: entity.getIn(['teams'])
            };
        },
        onChangeHandler: ({ entity, value }) => {
            return entity.setContact(value);
        }
    },
    {
        accessor: 'type',
        testId: locationMasterIDs.addNewLocation.selectType,
        label: 'locationForm.type',
        type: FieldTypeEnum.Select,
        required: true,
        placeholder: 'locationForm.typePlaceholder',
        options: LocationTypeOptions,
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value.value);
        }
    },
    {
        accessor: 'address.placeName',
        testId: locationMasterIDs.addNewLocation.inputPlaceName,
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'locationForm.placeName',
        helpText: 'locationForm.placeNameTooltip',
        placeholder: 'locationForm.placeNamePlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.formattedAddress',
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'locationForm.address',
        required: true,
        placeholder: 'locationForm.addressPlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.localityName',
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'locationForm.locality',
        placeholder: 'locationForm.localityPlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.city',
        label: 'locationForm.city',
        placeholder: 'locationForm.cityPlaceholder',
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        type: FieldTypeEnum.City,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: citiesApi.fetch.bind(citiesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.state',
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'locationForm.state',
        placeholder: 'locationForm.statePlaceholder',
        halfWidth: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'address.pincode',
        dependentAccessors: ['latLng', 'latLng.lat', 'latLng.lng'],
        supportsOnBlur: true,
        label: 'locationForm.pincode',
        placeholder: 'locationForm.pincodePlaceholder',
        type: FieldTypeEnum.Pincode,
        halfWidth: true,
        options: ({ field, props }) => {
            return {
                data: field.fetchInfo.response,
                countryCode: props.defaultCountryCode,
                isLoading: !field.fetchInfo.response
            };
        },
        extraProps: ({ t }) => {
            return {
                formatCreateLabel: formatCreateLabel(t)
            };
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value),
        fetchInfo: {
            fetcher: pincodesApi.fetch.bind(pincodesApi),
            params: ({ entity, props }) => {
                const countryCode = entity.getIn(['address', 'countryCode']);
                return { countryId: countryCode || props.defaultCountryCode };
            },
            response: null
        }
    },
    {
        accessor: 'address.countryCode',
        dependentAccessors: ['address.city', 'address.pincode'],
        label: 'locationForm.country',
        type: FieldTypeEnum.Select,
        required: true,
        options: CountryHelper.getCountryCodeOptions(),
        placeholder: 'locationForm.countryPlaceholder',
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        },
        extraProps: ({ field, props }) => {
            return {
                isSearchable: true,
                value: field.value || props.defaultCountryCode
            };
        }
    },
    {
        accessor: 'latLng',
        dependentAccessors: ['latLng.lat', 'latLng.lng'],
        type: FieldTypeEnum.Map,
        extraProps: ({ entity }) => {
            return {
                labelInfo: {
                    label: 'locationForm.locationMap',
                    autoDetect: 'locationForm.autoDetectLocation'
                },
                address: entity.getIn(['address'])
            };
        }
    },
    {
        accessor: 'latLng.lat',
        dependentAccessors: ['latLng'],
        label: 'locationForm.lat',
        type: FieldTypeEnum.Text,
        placeholder: 'locationForm.latPlaceholder',
        halfWidth: true
    },
    {
        accessor: 'latLng.lng',
        dependentAccessors: ['latLng'],
        label: 'locationForm.lng',
        type: FieldTypeEnum.Text,
        placeholder: 'locationForm.lngPlaceholder',
        halfWidth: true
    },
    {
        accessor: 'transactionDuration',
        label: 'locationForm.tat',
        helpText: 'locationForm.tatTooltip',
        type: FieldTypeEnum.TransactionDuration,
        halfWidth: true,
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'timeSlots',
        label: 'locationForm.slots',
        type: FieldTypeEnum.MultiDaySlots,
        helpText: 'locationForm.slotsTooltip',
        extraProps: ({ t }) => {
            return {
                translationsObject: {
                    add: t('locationForm.addSlots'),
                    change: t('locationForm.changeSlots')
                }
            };
        },
        getProcessedValueForField: ({ entity }) =>
            entity.initializeWeekSlots().timeSlots
    },
    {
        accessor: 'multiDaySlotSpan',
        type: FieldTypeEnum.Text,
        label: 'locationForm.multiDaySlotSpan',
        placeholder: 'locationForm.multiDaySlotSpanPlaceholder',
        helpText: 'locationForm.multiDaySlotSpanHelpText'
    },
    {
        accessor: 'skills',
        type: FieldTypeEnum.Skills,
        options: ({ field }) => {
            const skills = field.fetchInfo.response?.skills || [];
            return skills && skills.map(skill => Skill.fromJS(skill));
        },
        extraProps: ({ entity, field, t }) => {
            return {
                translationsObject: {
                    label: t('locationForm.skills'),
                    placeholder: t('locationForm.skillSelectPlaceholder'),
                    addSkillsText: t('locationForm.addSkill')
                },
                isLoading: !field.fetchInfo.response,
                teams: entity.getIn(['teams'])
            };
        },
        fetchInfo: {
            fetcher: skillsApi.fetch.bind(skillsApi),
            params: ({ entity, props }) => {
                const params = { clientId: props.clientId };

                if (entity) {
                    const teamIds = entity
                        .getIn(['teams'])
                        .map(teamId => teamId.teamId)
                        .toArray();

                    if (teamIds.length > 0) {
                        params.team = teamIds.concat(TeamEnum.NoTeam).join(',');
                    }
                }

                return params;
            },
            response: null
        }
    },
    {
        accessor: 'priority',
        type: FieldTypeEnum.Text,
        label: 'locationForm.priority',
        placeholder: 'locationForm.priorityPlaceholder',
        helpText: 'locationForm.priorityHelpText'
    },
    {
        accessor: 'crossDockInfo.serviceable',
        label: 'locationForm.crossDockServiceable',
        type: FieldTypeEnum.Boolean,
        onChangeHandler: ({
            entity,
            value
        }: {
            entity: MasterLocation,
            value: boolean
        }) => {
            const { crossDockInfo } = entity;
            let updatedCrossDockInfo = crossDockInfo.setServiceable(value);
            if (!value) {
                updatedCrossDockInfo = updatedCrossDockInfo
                    .setCrossDockIds(Immutable.List())
                    .setCrossDocks(Immutable.List());
            }
            return entity.setCrossDockInfo(updatedCrossDockInfo);
        },
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('translations:boolean.yes')
                    : t('translations:boolean.no')
            };
        }
    },
    {
        accessor: 'crossDockInfo.crossDockIds',
        label: 'locationForm.crossDockIds',
        placeholder: 'locationForm.crossDockIdPlaceholder',
        helpText: 'locationForm.crossDockIdTooltip',
        component: HomebaseIdInput,
        isHidden: ({ isFieldHidden }) => isFieldHidden('crossDockId'),
        extraProps: ({
            t,
            entity: {
                clientId,
                crossDockInfo: { crossDocks, serviceable },
                teams
            },
            field
        }: {
            entity: MasterLocation
        }) => {
            return {
                value: crossDocks.toJS(),
                homebaseInputFilters: input =>
                    crossDockIdInputFilters(input, teams),
                placeholder: t('locationForm.crossDockIdPlaceholder'),
                clientId,
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage,
                isMulti: true,
                disabled: !serviceable
            };
        },
        onChangeHandler: ({
            entity,
            value: homebases
        }: {
            entity: MasterLocation,
            value: Array<Object>
        }) => {
            const homebaseIds = Immutable.List(
                homebases.map(homebase => {
                    return HomebaseId.fromJS({
                        clientId: entity.clientId,
                        homebaseId: homebase.id
                    });
                })
            );

            const homebaseList = Immutable.List(
                homebases.map(homebase => {
                    return MasterHomebase.fromJS(homebase);
                })
            );
            const crossDockInfo = entity.crossDockInfo
                .setCrossDockIds(homebaseIds)
                .setCrossDocks(homebaseList);

            return entity.setCrossDockInfo(crossDockInfo);
        }
    },
    {
        accessor: 'vehicleModelId',
        label: 'locationForm.vehicleModelId',
        placeholder: 'locationForm.vehicleModelIdPlaceholder',
        helpText: 'locationForm.vehicleModelIdTooltip',
        component: VehicleModelIdInput,
        isHidden: ({ isFieldHidden }) => isFieldHidden('vehicleModelId'),
        extraProps: ({ entity: { vehicleModel } }) => {
            return {
                vehicleModelFilters: vehicleModelIdInputFilters,
                vehicleModel
            };
        },
        onChangeHandler: ({ entity, value: vehicleModel }) => {
            let vehicleModelId = null;
            if (vehicleModel) {
                vehicleModelId = VehicleModelId.fromJS({
                    clientId: entity.clientId,
                    modelId: vehicleModel.id
                });
            }
            return entity
                .setVehicleModelId(vehicleModelId)
                .setVehicleModel(vehicleModel);
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'locationSettings'
            };
        }
    }
];

const LOCATION_INITIAL_FETCH_FIELDS = [
    'skills',
    'address.city',
    'address.pincode'
];

const SingleLocationEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterLocation,
    MasterActions: LocationActions,
    EntityFetchAPI: locationApi,
    INITIAL_FETCH_FIELDS: LOCATION_INITIAL_FETCH_FIELDS,
    FIELDS: LOCATION_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'locationId',
        ENTITY_SETTINGS_NAME: 'locationSettings'
    },
    extraApiParams: {
        include: [
            IncludeTypeEnums.CrossDock,
            IncludeTypeEnums.VehicleModel,
            IncludeTypeEnums.Contact
        ]
    },
    mapEntityStateToProps: ({ state, props, baseProps }) => {
        const teams = teamsSelector(state, props);
        const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
            state,
            props
        );
        let defaultCountryCode = null;
        if (baseProps.localeSettings) {
            defaultCountryCode = baseProps.localeSettings.countryCode;
        }

        return {
            teams,
            defaultCountryCenter,
            defaultCountryCode
        };
    }
});

export default SingleLocationEditContainer;
